import { useCallback, useEffect, useMemo, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { acquireGraphAccessToken, GraphAuthRedirectPendingError, } from '@/services/graph/acquireGraphToken';
import { fetchInboxMessages } from '@/services/graph/fetchInbox';
import { mapGraphMessageToCommunicationBase } from '@/services/graph/mapMessage';
import { classifyMessages, mergeClassificationOntoBase, } from '@/services/classifyApi';
function mergeWorkflow(incoming, previousById) {
    const prev = previousById.get(incoming.graphMessageId);
    if (!prev)
        return incoming;
    return {
        ...incoming,
        validationStatus: prev.validationStatus,
        validatedAt: prev.validatedAt,
        validatedCategory: prev.validatedCategory,
        lastActionAt: prev.lastActionAt,
        category: prev.validationStatus === 'validated' && prev.validatedCategory
            ? prev.validatedCategory
            : incoming.category,
        categories: prev.validationStatus === 'validated' && prev.validatedCategory
            ? [
                prev.validatedCategory,
                ...incoming.categories.filter((c) => c !== prev.validatedCategory),
            ]
            : incoming.categories,
    };
}
function asUnclassified(base) {
    return {
        ...base,
        category: 'General',
        categories: ['General'],
        urgencyScore: 0,
        urgencyStatus: 'low',
        fraudScore: 0,
        fraudFlags: [],
        riskFlags: [],
        aiSummary: 'Classification pending — AI service unavailable for this message.',
        intent: undefined,
        priority: 'low',
        confidence: undefined,
        sentiment: undefined,
        requiresReply: undefined,
        entities: [],
        attachmentInsights: [],
        categorySync: false,
        validationStatus: 'pending',
    };
}
export function useGraphInbox() {
    const { instance, accounts, inProgress } = useMsal();
    const [communications, setCommunications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const homeAccountId = useMemo(() => {
        const acc = instance.getActiveAccount() ?? accounts[0];
        return acc?.homeAccountId ?? '';
    }, [instance, accounts]);
    const loadLiveInbox = useCallback(async () => {
        const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
        if (!account) {
            setCommunications([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const token = await acquireGraphAccessToken(instance, account);
            const messages = await fetchInboxMessages(token);
            const bases = messages.map((m) => mapGraphMessageToCommunicationBase(m));
            if (bases.length === 0) {
                setCommunications([]);
                setError('Outlook returned 0 messages for this account. Send a test email and refresh.');
                return;
            }
            const byId = new Map();
            try {
                const { classifications, errors } = await classifyMessages(token, bases.map((b) => ({
                    id: b.graphMessageId,
                    subject: b.subject,
                    bodyPreview: b.bodyPreview,
                })));
                for (const result of classifications) {
                    const base = bases.find((b) => b.graphMessageId === result.messageId);
                    if (!base)
                        continue;
                    byId.set(result.messageId, mergeClassificationOntoBase(base, result));
                }
                if (errors.length > 0) {
                    console.warn('[inbox] classify errors', errors);
                }
            }
            catch (classifyErr) {
                console.warn('[inbox] classifier unavailable', classifyErr);
                setError(classifyErr instanceof Error
                    ? `Inbox loaded from Outlook (${bases.length} messages), but AI classification failed: ${classifyErr.message}.`
                    : 'Inbox loaded from Outlook, but AI classification failed.');
            }
            setCommunications((prev) => {
                const prevMap = new Map(prev.map((c) => [c.graphMessageId, c]));
                return bases.map((base) => {
                    const enriched = byId.get(base.graphMessageId) ?? asUnclassified(base);
                    return mergeWorkflow(enriched, prevMap);
                });
            });
        }
        catch (e) {
            if (e instanceof GraphAuthRedirectPendingError) {
                setError('Redirecting to Microsoft to grant mailbox access. After you accept, you will return here and the inbox will load.');
                return;
            }
            const msg = e instanceof Error ? e.message : 'Failed to load inbox';
            setError(msg);
            setCommunications([]);
        }
        finally {
            setLoading(false);
        }
    }, [instance]);
    const refresh = useCallback(async () => {
        await loadLiveInbox();
    }, [loadLiveInbox]);
    useEffect(() => {
        if (inProgress !== InteractionStatus.None)
            return;
        if (!homeAccountId)
            return;
        const handle = window.setTimeout(() => {
            void loadLiveInbox();
        }, 0);
        return () => window.clearTimeout(handle);
    }, [homeAccountId, inProgress, loadLiveInbox]);
    return {
        communications,
        setCommunications,
        loading,
        error,
        refresh,
    };
}
